import { useEffect, RefObject } from 'react';

interface UseScrollToActiveItemParams {
  activeItemId: string | null;
  containerRef: RefObject<HTMLElement>;
  itemRefs: RefObject<Map<string, HTMLElement>>;
  items: Array<{ id: string }>;
  scrollDelay?: number;
  padding?: number;
}

const useScrollToActiveItem = ({
  activeItemId,
  containerRef,
  itemRefs,
  items,
  scrollDelay = 200,
  padding = 10,
}: UseScrollToActiveItemParams) => {
  useEffect(() => {
    if (!activeItemId || !containerRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const activeItem = itemRefs.current?.get(activeItemId);
      if (!activeItem || !containerRef.current) {
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      const elementRect = activeItem.getBoundingClientRect();
      const isVisibleHorizontally =
        elementRect.left >= containerRect.left - padding && elementRect.right <= containerRect.right + padding;

      if (isVisibleHorizontally) {
        return;
      }

      const isLastItem = items[items.length - 1]?.id === activeItemId;
      activeItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: isLastItem ? 'end' : 'center',
      });
    }, scrollDelay);

    return () => clearTimeout(timeoutId);
  }, [activeItemId, items, containerRef, itemRefs, scrollDelay, padding]);
};

export default useScrollToActiveItem;
